import { formatClientAddress, type ClientAddressPayload } from './format-client-address';

/**
 * Structured address fields retained for edit forms and detail rows.
 */
export type ClientAddressFields = {
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zip_code: string;
};

/**
 * Complementary identity / personal / financial fields from the nested API client.
 */
export type ClientDetailExtras = {
  nationality: string;
  profession: string;
  marital_status: string;
  rg: string;
  issuing_agency: string;
  trade_name: string;
  corporate_name: string;
  address: string;
  address_fields: ClientAddressFields;
  monthly_income: number | undefined;
  income_proof: string;
};

/**
 * Reads a trimmed string field from a record.
 *
 * @param source - Source object.
 * @param key - Field name.
 * @returns Trimmed string, or empty.
 */
function textField(source: Record<string, unknown> | undefined, key: string): string {
  const value = source?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Parses nested API address into form-friendly fields.
 *
 * @param address - Nested address VO from the API.
 * @returns Normalized address fields (empty strings when absent).
 */
export function parseClientAddressFields(address: unknown): ClientAddressFields {
  if (address == null || typeof address !== 'object') {
    return {
      street: '', number: '', complement: '', neighborhood: '', city: '', state: '', zip_code: '',
    };
  }
  const a = address as ClientAddressPayload;
  return {
    street: textField(a as Record<string, unknown>, 'street'),
    number: textField(a as Record<string, unknown>, 'number'),
    complement: textField(a as Record<string, unknown>, 'complement'),
    neighborhood: textField(a as Record<string, unknown>, 'neighborhood'),
    city: textField(a as Record<string, unknown>, 'city'),
    state: textField(a as Record<string, unknown>, 'state'),
    zip_code: textField(a as Record<string, unknown>, 'zip_code'),
  };
}

/**
 * Extracts complementary personal, address and financial fields from a nested client payload.
 *
 * @param client - Raw nested API client.
 * @returns Normalized extras for ClientRow / edit defaults.
 */
export function extractClientDetailExtras(client: Record<string, unknown>): ClientDetailExtras {
  const personalInfo = (client.personal_info as Record<string, unknown>) ?? {};
  const identity = (client.identity as Record<string, unknown>) ?? {};
  const financial = (client.financial as Record<string, unknown>) ?? {};
  const address_fields = parseClientAddressFields(client.address);
  const incomeRaw = financial.monthly_income;
  const monthly_income =
    typeof incomeRaw === 'number'
      ? incomeRaw
      : typeof incomeRaw === 'string' && incomeRaw.trim()
        ? Number(incomeRaw.replace(/\D/g, ''))
        : undefined;

  return {
    nationality: textField(personalInfo, 'nationality'),
    profession: textField(personalInfo, 'profession'),
    marital_status: textField(personalInfo, 'marital_status'),
    rg: textField(identity, 'rg'),
    issuing_agency: textField(identity, 'issuing_agency'),
    trade_name: textField(identity, 'trade_name'),
    corporate_name: textField(identity, 'corporate_name'),
    address: formatClientAddress(client.address),
    address_fields,
    monthly_income: Number.isFinite(monthly_income) ? monthly_income : undefined,
    income_proof: textField(financial, 'income_proof'),
  };
}
